import { useMemo, useState, type CSSProperties } from "react";
import { confettiPieces } from "./confetti";
import { PixelHeart } from "./PixelHeart";
import { PixelTurtle } from "./PixelTurtle";

/** The app's own lock, wearing a birthday.
 *
 *  Access gates the tunnel so this page is not reachable by a stranger from the
 *  internet; this password is what still protects the archive over Tailscale,
 *  where Access is not in the path. All three error paths below are load-bearing
 *  for that, and the decoration is not allowed to swallow any of them.
 *
 *  `data-theme` is on THIS element, not on <html>. The birthday page is the
 *  gift, not one of the three modes, so it stays Minecraft whatever the
 *  timeline is set to -- and scoping it here means it does not disturb the mode
 *  the timeline restores a second later.
 *
 *  There is no username field and nothing on screen says "password". One
 *  person, one box. The input is still `type="password"` with
 *  `autoComplete="current-password"`: attributes, invisible to the reader, and
 *  what keeps the characters masked and a password manager working.
 */
export function Login({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Seeded and memoised: a wrong password re-renders this component, and
  // reshuffling every square under the reader's eyes reads as a glitch.
  const pieces = useMemo(() => confettiPieces(40, 25), []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setPassword("");
        onSuccess();
        return;
      }
      // 429 is worth spelling out: five wrong tries locks this IP for 15
      // minutes, and "wrong password" would send you hunting for a typo.
      setError(res.status === 429 ? "Too many attempts — try again in 15 minutes."
                                  : "Wrong password.");
    } catch {
      setError("Could not reach the server.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="birthday" data-theme="minecraft">
      <div className="confetti" aria-hidden="true">
        {pieces.map((p, i) => (
          <i
            key={i}
            style={{
              "--left": `${p.left}vw`,
              "--size": `${p.size}px`,
              "--delay": `${p.delay}s`,
              "--dur": `${p.dur}s`,
              "--drift": `${p.drift}px`,
              "--spin": `${p.spin}deg`,
              background: p.colour,
            } as CSSProperties}
          />
        ))}
      </div>

      <div className="gift">
        {/* The <333 is its own element, not a newline in the string. A text
            node's whitespace collapses, so "\n" would render as a space and
            the second line would exist only in the source. */}
        <h1>
          Happy 25th Birthday
          <span className="hearts">&lt;333</span>
        </h1>
        <PixelHeart />
        <p className="line">I can not give you much rn, but I am giving you my heart</p>

        <form onSubmit={submit}>
          <input
            type="password"
            aria-label="unlock the archive"
            value={password}
            autoFocus
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            style={{ fontSize: 16 }}
          />
          {/* 44pt tap target, and 16px above so iOS does not zoom on focus. */}
          <button type="submit" disabled={busy || !password}>
            {busy ? "…" : "Unlock"}
          </button>
        </form>

        {error && <p role="alert">{error}</p>}
      </div>

      {/* Three of them, bobbing out of step so the row does not read as one
          animation applied to a copy. Decoration, so aria-hidden: a screen
          reader announcing turtles between the message and the box you have to
          type in is not a gift. */}
      <div className="turtles" aria-hidden="true">
        <PixelTurtle />
        <PixelTurtle />
        <PixelTurtle />
      </div>
    </main>
  );
}
